/**
 * 触感反馈 hook
 * - iOS Safari 16.4+: navigator.vibrate() 实际触发 Taptic Engine
 * - Android Chrome: 标准 Vibration API
 * - 其他浏览器: silent no-op
 *
 * 不同 pattern 对应不同强度:
 * - light:  10ms  - 普通按钮点击
 * - medium: 20ms  - 完成、滑动到位
 * - heavy:  30ms  - 重要操作 (删除、长按)
 * - success: [10, 50, 10] - 任务完成
 * - warning: [80] - 警告
 * - error:   [50, 50, 50] - 错误
 * - selection: 5ms - 切换选项
 */

export type HapticPattern =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'success'
  | 'warning'
  | 'error'
  | 'selection';

const PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
  success: [10, 50, 10],
  warning: 80,
  error: [50, 50, 50],
  selection: 5,
};

/** 触发一次触感反馈 */
export function haptic(pattern: HapticPattern = 'light'): void {
  // 检查 navigator.vibrate 是否可用
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return; // 桌面/老浏览器: 静默
  }
  try {
    navigator.vibrate(PATTERNS[pattern]);
  } catch {
    // 某些浏览器 (尤其 iOS < 16.4) 抛异常
  }
}

/** React hook 版本: 返回 trigger 函数 */
export function useHaptics() {
  return {
    trigger: haptic,
    light: () => haptic('light'),
    medium: () => haptic('medium'),
    heavy: () => haptic('heavy'),
    success: () => haptic('success'),
    warning: () => haptic('warning'),
    error: () => haptic('error'),
    selection: () => haptic('selection'),
  };
}
